import { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { patientIntakeApi, type ContinuationChannel } from "@/lib/patientIntakeApi";
import { hydrateIntakeData } from "@/lib/hydrateIntakeData";
import type { IntakeData } from "@/lib/types";
import IntakeV2Page from "./IntakeV2Page";
import { VerificationCard } from "./cards/VerificationCard";
import type { CardDef } from "./types";

/**
 * `/{locale}/continue/:formUid` — a patient who started intake by talking to
 * Jenny finishes the form in the browser.
 *
 * The form UID in the URL is a locator, never authorization: nothing about the
 * patient is rendered or fetched until the one-time code is verified. Failures
 * are reported with one neutral message that does not disclose whether the form
 * exists or who it belongs to.
 */
export default function ContinuePage() {
  const { formUid } = useParams<{ formUid: string }>();
  const { i18n } = useLingui();

  const [channel, setChannel] = useState<ContinuationChannel>("email");
  const [verified, setVerified] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hydratedRef = useRef<Partial<IntakeData> | null>(null);

  const genericFailure = i18n._(
    msg`We couldn't verify that code. Please check the code and try again.`,
  );

  const card = useMemo<CardDef>(
    () => ({
      id: "verification",
      cardType: "verification",
      question: i18n._(msg`Let's pick up where you left off`),
      hint:
        channel === "sms"
          ? i18n._(msg`Enter the 6-digit code we sent by text.`)
          : i18n._(
              msg`Enter the 6-digit code we sent to the email on your intake.`,
            ),
    }),
    [channel, i18n],
  );

  const sendCode = useCallback(async () => {
    if (!formUid) throw new Error(genericFailure);
    try {
      const result = await patientIntakeApi.startContinuation(formUid, {
        channel,
      });
      return { challengeId: result?.data?.challengeId };
    } catch {
      // Never surface the API's reason: it can leak whether the form exists.
      throw new Error(
        i18n._(
          msg`We couldn't send a code right now. Please try again in a moment.`,
        ),
      );
    }
  }, [channel, formUid, genericFailure, i18n]);

  const verifyCode = useCallback(
    async (code: string) => {
      if (!formUid) throw new Error(genericFailure);
      let verified;
      try {
        verified = await patientIntakeApi.verifyContinuation(formUid, code);
      } catch {
        throw new Error(genericFailure);
      }

      // Same storage the trusted-session bootstrap uses.
      try {
        if (verified.accessToken) {
          localStorage.setItem("accessToken", verified.accessToken);
        }
        if (verified.refreshToken) {
          localStorage.setItem("refreshToken", verified.refreshToken);
        }
      } catch {
        // Ignore storage errors (e.g. private mode)
      }

      // Only now may we touch the patient's form.
      let form = verified.form as Record<string, unknown> | undefined;
      if (!form) {
        form = (await patientIntakeApi.getFormByUid(formUid)) as Record<
          string,
          unknown
        >;
      }

      hydratedRef.current = {
        ...hydrateIntakeData(form),
        formUid,
        emailVerified: true,
      };
    },
    [formUid, genericFailure],
  );

  const handleVerified = useCallback(() => {
    if (!hydratedRef.current) {
      setLoadError(genericFailure);
      return;
    }
    setVerified(true);
  }, [genericFailure]);

  if (verified && hydratedRef.current) {
    return (
      <IntakeV2Page initialData={hydratedRef.current} resumeAtFirstIncomplete />
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#F5FAFF] px-4 py-10">
      <VerificationCard
        // Remount so a channel switch requests a fresh code.
        key={channel}
        card={card}
        data={{ email: "" } as IntakeData}
        onUpdate={() => {}}
        onSuccess={handleVerified}
        onBack={() => {}}
        formUid={formUid}
        maskedDestination={
          channel === "sms"
            ? i18n._(msg`the phone number on your intake`)
            : i18n._(msg`the email on your intake`)
        }
        sendCode={sendCode}
        verifyCode={verifyCode}
      />

      {channel === "email" && (
        <button
          type="button"
          onClick={() => setChannel("sms")}
          className="mt-4 text-sm font-semibold text-[#166B93] underline underline-offset-4 hover:text-[#125576]"
        >
          {i18n._(msg`Send a code by text instead`)}
        </button>
      )}

      {loadError && (
        <p className="mt-4 text-sm text-red-600" role="alert">
          {loadError}
        </p>
      )}
    </div>
  );
}
